import React, { useEffect, useState, useRef } from 'react'

import clsx from 'clsx'

import useViewport from 'hooks/useViewport'
import { handleScrollTo } from 'screens/compare/components/NavigationList/helpers'

import { extractHeadings } from './helpers/extractHeadings'

import classes from './TableOfContents.module.scss'

/** @type {import('./types').default} */
const TableOfContents = ({ contentfulData, stickyContainerRef = null }) => {
  const [selectedMenuItem, setSelectedMenuItem] = useState(null)
  /** @type {[import('./types').Heading[], Function]} */
  const [headings, setHeadings] = useState([])
  const { up } = useViewport()
  const isDesktop = up('md')

  useEffect(() => {
    if (contentfulData) {
      const extractedHeadings = extractHeadings(contentfulData)
      setHeadings(extractedHeadings)
    }
  }, [contentfulData])

  useEffect(() => {
    if (!isDesktop || headings.length === 0) return

    const observerCallback = entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.target?.id) {
          setSelectedMenuItem(entry.target.id)
          return
        }
      })
    }

    const observer = new IntersectionObserver(observerCallback, {
      rootMargin: '-25% 0px -40% 0px',
    })

    // Observe all heading elements that match our extracted headings
    headings.forEach(heading => {
      const element = document.getElementById(heading.id)
      if (element) {
        observer.observe(element)
      }
    })

    return () => observer.disconnect()
  }, [isDesktop, headings])

  const defaultRef = useRef(null)
  const containerRef = stickyContainerRef || defaultRef

  useEffect(() => {
    if (isDesktop) {
      const setSticky = async () => {
        const StickyFilter = (await import('components/Filters/stickyFilter.js')).default
        if (containerRef.current) {
          new StickyFilter(containerRef.current, {
            topSpacing: 94,
            bottomSpacing: 80,
          })
        }
      }

      if (containerRef.current) {
        setSticky()
      }
    }
  }, [containerRef, isDesktop])

  if (headings.length === 0) {
    return null
  }

  return (
    <div className={classes.root} ref={defaultRef}>
      <div className={classes.itemsList}>
        {/* Main category leaders link */}
        <a href="/category-leaders-methodology/" className={clsx(classes.item, classes.mainLink)}>
          Category Leaders
        </a>

        {/* Sub-links for headings */}
        <div className={classes.subMenu}>
          {headings
            .filter(heading => heading.level === 3) // Only show H3 headings like the backend
            .map(({ title, menuTitle, id }) => (
              <div key={id} className={classes.subLink}>
                <a
                  href={`#${id}`}
                  onClick={e => {
                    e.preventDefault()
                    handleScrollTo(id, 100)
                  }}
                  className={clsx(classes.item, {
                    [classes.active]: selectedMenuItem === id,
                  })}
                >
                  {menuTitle || title}
                </a>
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}

export default TableOfContents
